import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import vipIconSvg from '@assets/img/icons/vip-icon.svg';
import cn from 'classnames';

import { useActivateVipStatusMutation } from '@/api/queries/user';
import { AppRoute } from '@/common/app/routes';
import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { additionalDiscountForUsdt } from '@/common/config/common';
import { vipBenefitsConfig } from '@/common/config/vip-benefirs';
import { useApplePrice } from '@/common/hooks/apple-price';
import { usePaymentStatus } from '@/common/hooks/payment-status';
import { usePurchaseAttempt } from '@/common/hooks/purchase-attempt';
import { useTonAutoConnect } from '@/common/hooks/ton-auto-connect';
import { APPLE_VIP, isAppleIapAvailable } from '@/common/lib/apple-iap';
import { multiplication } from '@/common/lib/decimal';
import { openTgInvoice } from '@/common/lib/openTgInvoice';
import { SendAnalitics } from '@/common/lib/playdeck';
import { type ActivateVipStatusResponseType } from '@/common/types/query/user';
import { Benefits } from '@/components/OffersMarket/VipMarket/Benefits/Benefits';
import { H1, StringMain } from '@/components/Typography/Typography';
import { GreyBlurButton } from '@/components/ui/Button/Button';
import { Close } from '@/components/ui/Close/Close';
import RadialCircleGradient from '@/components/ui/Gradient/RadialCircleGradient';
import TONandStarsBuyButtons from '@/components/ui/TONandStarsBuyButtons/TONandStarsBuyButtons';
import PendingStatus from '@/components/ui/ViewStatusComponents/PendingStatus';
import SuccessfullStatus from '@/components/ui/ViewStatusComponents/SuccessfullStatus';
import { vipPeriodLabel, vipRenewalPeriod } from '@/components/Vip/vipPeriod';
import { changePaymentStatus } from '@/redux/reducers/paymentsReducer';
import {
  acitvateVipStatus,
  changeVipStatus,
  subtractBalance,
} from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import css from './style.module.scss';

import shinePng from '/img/circle-light-effect.png';
import shineWebp from '/img/circle-light-effect.webp';
import heroPng from '/img/shop/vip-hero.png';
import heroWebp from '/img/shop/vip-hero.webp';

interface props {
  days: number;
  price: number;
  starPrice: number;
  discount: number;
}

const BuyVip: React.FC<props> = ({ days, price, starPrice, discount }) => {
  const [activateVip, { isLoading }] = useActivateVipStatusMutation();
  const navigate = useNavigate();
  const isAppStore = isAppleIapAvailable();
  const applePrice = useApplePrice(price, days);
  const isSubscription = isAppStore && !!APPLE_VIP[days]?.subscription;
  const userLevel = useAppSelector((state) => state.user.user?.level);
  const { t } = useTranslation();
  const { closeModal, secondModal, thirdModal } = useContext(ModalContext);
  const connectWalletAndDoTransaction = useTonAutoConnect();
  const dispatch = useAppDispatch();
  const paymentStatus = usePaymentStatus();
  const usdPrice = useMemo(
    () => multiplication(price, additionalDiscountForUsdt.coefficient),
    [price],
  );

  usePurchaseAttempt({
    item: 'vip',
    item_id: days.toString(),
    price,
    currency: 'usdt',
  });

  const sendAnalitics = (currency: 'ton' | 'xtr' | 'usdt'): void => {
    SendAnalitics.purchaseSuccessfull({
      item: 'vip',
      item_id: days.toString(),
      price,
      currency,
    });
    SendAnalitics.getVipStatus({ level: userLevel || 0, type: 'paid' });
  };

  function calcBasePrice(): number {
    if (!discount && discount === 0) return price;
    const pricePercentage = 100 - discount;
    const multiplier = pricePercentage / 100;
    const basePrice = price / multiplier;

    return parseFloat(basePrice.toFixed(2));
  }

  const buyPaidVip = async (currency: 'XTR' | 'TON'): Promise<void> => {
    try {
      if (currency === 'TON') {
        connectWalletAndDoTransaction(
          async () =>
            (await activateVip({
              type: 'Paid',
              days,
              currency,
            }).unwrap()) as ActivateVipStatusResponseType,
          {
            onSuccess: () => {
              dispatch(changePaymentStatus('pending'));
              ToastNotification(t('Payment will be credited soon.'), {
                img: vipIconSvg,
              });
              sendAnalitics('ton');
            },
            onError: () => {
              dispatch(changePaymentStatus(undefined));
              ToastNotification(t('Something went wrong, please try again'), {
                img: vipIconSvg,
              });
            },
          },
        );
      } else {
        const response = await activateVip({
          type: 'Paid',
          days,
          currency,
        }).unwrap();

        // for ActivateVipStatusBuyLinkResponseType response type
        if ('link' in response) {
          openTgInvoice(response.link, {
            errorMessage: { image: { img: vipIconSvg } },
            onSuccess: () => {
              dispatch(changePaymentStatus('pending'));
              sendAnalitics('xtr');
            },
            playdeckRequestPayload: {
              amount: starPrice,
              description: 'buy vip',
              externalId: `buy-vip-${starPrice}`,
            },
          });
        }
      }
    } catch (error) {
      console.error(error);
      ToastNotification(t('Something went wrong, please try again'), {
        img: vipIconSvg,
      });
    }
  };

  const buyUsdt = async (): Promise<void> => {
    try {
      const data = await activateVip({
        days,
        type: 'Paid',
        currency: 'USDT',
      }).unwrap();

      if (thirdModal.isOpen) {
        thirdModal.closeModal();
      } else {
        secondModal.closeModal();
      }
      dispatch(changePaymentStatus('success'));
      sendAnalitics('usdt');
      dispatch(subtractBalance({ type: 'usdt', value: usdPrice }));
      dispatch(acitvateVipStatus(data as ActivateVipStatusResponseType));
      dispatch(changeVipStatus(true));
      ToastNotification(t('Success. Your VIP status activated'), {
        img: vipIconSvg,
      });
    } catch (error) {
      console.error(error);
      ToastNotification(t('Something went wrong, please try again'), {
        img: vipIconSvg,
      });
      dispatch(changePaymentStatus(undefined));
    }
  };

  const handleClose = (): void => {
    closeModal();
    dispatch(changePaymentStatus(undefined));
  };

  return (
    <div className={cn(css.buyVipModal)}>
      {paymentStatus === 'pending' ? (
        <PendingStatus />
      ) : paymentStatus === 'success' ? (
        <SuccessfullStatus className="w-[304px]">
          <div className="opacity-[0.6]">
            <picture>
              <source srcSet={heroWebp} type="image/webp" />
              <img src={heroPng} />
            </picture>
            <div className="absolute top-[50%] left-[50%] w-[120%] h-[100%] translate-x-[-50%] translate-y-[-50%] z-[-3]">
              <picture>
                <source srcSet={shineWebp} type="image/webp" />
                <img src={shinePng} alt="shine" className="h-full w-full" />
              </picture>
            </div>
            <RadialCircleGradient
              type="yellow"
              className="top-[50%] left-[50%] w-[120%] h-[100%] translate-x-[-50%] translate-y-[-50%] z-2 rounded-full"
            />
          </div>
        </SuccessfullStatus>
      ) : (
        <>
          <div className={css.wrapper}>
            <div className={css.title}>
              <H1>{t('Welcome to VIP CLUB')}</H1>
            </div>
            <div className="flex gap-6">
              <Benefits
                icon={vipBenefitsConfig.shopIcons[5]}
                transKey={vipBenefitsConfig.shopTransKeyObjBenefits[5](t)}
              />
              <Benefits
                icon={vipBenefitsConfig.shopIcons[6]}
                transKey={vipBenefitsConfig.shopTransKeyObjBenefits[6](t)}
              />
            </div>
            <div className={cn(css.imageBlock)}>
              <picture>
                <source srcSet={heroWebp} type="image/webp" />
                <img src={heroPng} />
              </picture>
              <div className="absolute top-[50%] left-[50%] w-[120%] h-[100%] translate-x-[-50%] translate-y-[-50%] z-[-3]">
                <picture>
                  <source srcSet={shineWebp} type="image/webp" />
                  <img src={shinePng} alt="shine" className="h-full w-full" />
                </picture>
              </div>
              <RadialCircleGradient
                type="yellow"
                className="top-[50%] left-[50%] w-[120%] h-[100%] translate-x-[-50%] translate-y-[-50%] z-2 rounded-full"
              />
            </div>
            <div className="w-full flex justify-center">
              <StringMain weight="bold" className="text-[24px]">
                {isAppStore
                  ? `${vipPeriodLabel(days, t).toUpperCase()} for ${applePrice}`
                  : `${vipPeriodLabel(days, t).toUpperCase()} for $ ${price}`}
              </StringMain>
            </div>
          </div>
          <div className="flex flex-col mt-5 px-6 self-stretch gap-3">
            <TONandStarsBuyButtons
              onClick={buyPaidVip}
              isLoading={isLoading}
              days={days}
              appleVipDays={days}
              usdPrice={usdPrice}
              discount={additionalDiscountForUsdt.percentage}
              price={price}
              starPrice={starPrice}
              mustBeUsdtButton={true}
              onUsdtBuy={buyUsdt}
              fullPrice={discount ? calcBasePrice() : undefined}
              variant="switch"
              classNames="!px-0"
            />
            {isAppStore && (
              // App Store guideline 3.1.2: auto-renewal terms at the point of sale
              <p className="text-[11px] leading-snug text-white/50 text-center">
                {isSubscription
                  ? t(
                      'VIP subscription: {{price}} {{period}}. Renews automatically unless cancelled at least 24 hours before the end of the period. Payment is charged to your Apple ID; manage or cancel it in your App Store account settings.',
                      {
                        price: applePrice,
                        period: vipRenewalPeriod(days, t),
                      },
                    )
                  : t('One-time purchase. Does not renew automatically.')}{' '}
                <span
                  className="underline"
                  onClick={() => {
                    handleClose();
                    navigate(AppRoute.TERMS);
                  }}
                >
                  {t('Terms of Use')}
                </span>
                {' · '}
                <span
                  className="underline"
                  onClick={() => {
                    handleClose();
                    navigate(AppRoute.PRIVACY);
                  }}
                >
                  {t('Privacy Policy')}
                </span>
              </p>
            )}
            <GreyBlurButton onClick={handleClose} text={t('Close')} />
          </div>
        </>
      )}
      {paymentStatus === 'pending' && (
        <Close
          onClick={handleClose}
          imageVariant="modal"
          size="lg"
          className="absolute bottom-0"
        />
      )}
    </div>
  );
};

export default BuyVip;
